import { useState } from "react";
import { useTranslation } from "react-i18next";

export default function SubmitAndNewsletterSection() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [newsletterStatus, setNewsletterStatus] = useState<"idle" | "success" | "error">("idle");
  const [newsletterMsg, setNewsletterMsg] = useState("");

  const handleNewsletter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    try {
      const body = new URLSearchParams({ email });
      const res = await fetch("https://readdy.ai/api/form/d7b5cq68177dosp0is60", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setNewsletterStatus("success");
        setNewsletterMsg(t("newsletter.success"));
        setEmail("");
      } else {
        setNewsletterStatus("error");
        setNewsletterMsg(t("newsletter.error"));
      }
    } catch {
      setNewsletterStatus("error");
      setNewsletterMsg(t("newsletter.error"));
    }
  };

  const stats = [
    { icon: "ri-eye-line", value: "120K+", labelKey: "submit.stat.readers" },
    { icon: "ri-share-line", value: "50K+", labelKey: "submit.stat.shares" },
    { icon: "ri-global-line", value: "54", labelKey: "submit.stat.countries" },
  ];

  return (
    <>
      <section className="py-20 bg-gradient-to-r from-orange-500 via-orange-400 to-yellow-400 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src="https://public.readdy.ai/ai/img_res/3fa38c6e8b424c5f13b82d1ff122aa77.jpg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-10">
            <div className="text-center lg:text-left max-w-xl">
              <div className="inline-flex items-center gap-2 bg-white/20 text-white text-xs font-bold px-4 py-2 rounded-full mb-4 uppercase tracking-wider">
                <i className="ri-pencil-line"></i>
                {t("submit.badge")}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white font-serif leading-tight mb-4">{t("submit.title")}</h2>
              <p className="text-white/80 text-base md:text-lg mb-8">{t("submit.subtitle")}</p>
              <a href="mailto:contact@waziconsulting.com" className="inline-flex items-center gap-3 bg-white text-orange-500 font-bold px-8 py-4 rounded-full hover:bg-orange-50 transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap">
                <i className="ri-mail-send-line text-xl"></i>
                {t("submit.cta")}
              </a>
            </div>
            <div className="flex flex-wrap justify-center sm:justify-start gap-4 sm:gap-6 text-center">
              {stats.map((s) => (
                <div key={s.labelKey} className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 md:p-6 min-w-[110px] md:min-w-[130px]">
                  <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
                    <i className={`${s.icon} text-white text-2xl`}></i>
                  </div>
                  <div className="text-white font-bold text-2xl">{s.value}</div>
                  <div className="text-white/70 text-sm">{t(s.labelKey)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="newsletter" className="py-16 bg-gray-900">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-10 h-10 flex items-center justify-center mx-auto mb-4 bg-orange-500 rounded-full">
              <i className="ri-mail-line text-white text-xl"></i>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white font-serif mb-3">{t("newsletter.title")}</h2>
            <p className="text-gray-400 mb-8">{t("newsletter.subtitle")}</p>
            {newsletterStatus === "success" ? (
              <div className="bg-green-500/20 border border-green-500/30 text-green-400 rounded-2xl px-6 py-4 text-sm font-medium">
                <i className="ri-checkbox-circle-line mr-2"></i>
                {newsletterMsg}
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleNewsletter} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("newsletter.placeholder")}
                  required
                  className="flex-1 bg-gray-800 border border-gray-700 text-white placeholder-gray-500 rounded-full px-5 py-3 text-sm focus:outline-none focus:border-orange-500 transition-colors"
                />
                <button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-3 rounded-full transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2 justify-center">
                  <i className="ri-send-plane-line"></i>
                  {t("newsletter.cta")}
                </button>
              </form>
            )}
            {newsletterStatus === "error" && <p className="text-red-400 text-sm mt-3">{newsletterMsg}</p>}
            <p className="text-gray-600 text-xs mt-4">{t("newsletter.legal")}</p>
          </div>
        </div>
      </section>
    </>
  );
}
